import React, { ReactNode, useLayoutEffect, useRef } from 'react';

const StyleHtml = ({ children }: { children: ReactNode }) => {
  const elRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (elRef.current) {
      const preCodeBlocks = elRef.current.querySelector('pre');
      const codeBlock = preCodeBlocks?.querySelector('code');

      if (codeBlock) {
        // Create a wrapper only if the parent is not already wrapped
        const wrapperDiv = document.createElement('div');

        // Apply custom styles to the wrapperDiv
        wrapperDiv.style.display = 'flex';
        wrapperDiv.style.alignItems = 'center';
        wrapperDiv.style.borderRadius = '4px';
        wrapperDiv.style.border = '1px solid var(--Stroke-Medium, #B7B7B7)';
        wrapperDiv.style.background = 'var(--Skills-Gray-Lighter, #F8F8F8)';
        wrapperDiv.style.padding = '16px';
        // Extract text from the <code> element
        const codeText = codeBlock.textContent || codeBlock.innerText;

        // Create a new element to hold the code text
        const codeTextElement = document.createElement('span');
        codeTextElement.textContent = codeText; // Set the text content

        // Apply custom styles for the code text
        codeTextElement.style.color = 'var(--Skills-Auxiliary-Yellow-700, #9F741F)';
        codeTextElement.style.fontFamily = '"Nunito Sans"';
        codeTextElement.style.fontSize = '12px';
        codeTextElement.style.fontStyle = 'normal';
        codeTextElement.style.fontWeight = '700';

        // Append the code text element to the wrapper
        wrapperDiv.appendChild(codeTextElement);

        // Replace the <pre><code> structure with the new wrapper
        preCodeBlocks?.parentNode?.insertBefore(wrapperDiv, preCodeBlocks);
        preCodeBlocks?.remove(); // Remove the original <pre> (and <code> tag)
      }

      const tableFigures = elRef.current.querySelectorAll('figure.table');
      tableFigures.forEach((figure) => {
        const tableEle = figure.querySelector('table');
        if (tableEle) {
          const tableWrapper = document.createElement('div');
          tableWrapper.style.overflowX = 'auto';
          tableWrapper.style.display = 'flex';
          figure.parentElement?.insertBefore(tableWrapper, figure);
          tableWrapper.appendChild(figure);
        }
      });

      // for rendering media
      const oembeds = elRef.current.querySelectorAll('oembed');
      oembeds.forEach((oembed) => {
        const url = oembed.getAttribute('url');
        if (url) {
          const videoId = url.split('v=')[1] || url.split('/')[url.split('/').length - 1];
          const iframe = document.createElement('iframe');
          if (url?.includes('vimeo')) iframe.src = `https://player.vimeo.com/video/${videoId}`;
          else iframe.src = `https://www.youtube.com/embed/${videoId}`;

          iframe.allow =
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.style.width = '100%';
          iframe.style.height = '100%';
          iframe.style.position = 'absolute';
          iframe.style.top = '0';
          iframe.style.left = '0';
          iframe.style.bottom = '0';
          iframe.style.right = '0';
          iframe.style.display = 'block';

          const figure = document.createElement('figure');
          figure.classList.add('media');
          figure.style.position = 'relative'; // Ensure relative positioning
          figure.style.width = '100%'; // Set figure width to 100%
          figure.style.paddingTop = '56.25%'; // 16:9 aspect ratio (9 / 16 * 100%)
          figure.appendChild(iframe);

          oembed.replaceWith(figure);
        }
      });

      // Handle anchor tags to open in new tab
      const anchorTags = elRef.current.querySelectorAll('a');
      anchorTags.forEach((anchor) => {
        const href = anchor.getAttribute('href') || '';

        const isInternal = href.startsWith('/') || href.includes('pwskills.com');

        if (isInternal) {
          anchor.removeAttribute('target');
          anchor.removeAttribute('rel');
        } else {
          anchor.setAttribute('target', '_blank');
          anchor.setAttribute('rel', 'nofollow');
        }
      });
    }
  }, [children]);

  return (
    <div ref={elRef} className="style-html w-full">
      {children}
    </div>
  );
};

export default StyleHtml;
